import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiEdit2, FiTrash2, FiSearch, FiFilter, FiMail, FiBell, FiX, FiCheck, FiCheckCircle } from 'react-icons/fi';
import FabMenu from '../Notification/FabMenu';
import axios from 'axios';
import { Form, InputGroup, Badge, Spinner, Button, Modal, Toast, ToastContainer } from 'react-bootstrap';

const Template = () => {
    const [refreshKey, setRefreshKey] = useState(0);
    const [categories, setCategories] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [templateSearch, setTemplateSearch] = useState('');
    const [activeCategoryFilter, setActiveCategoryFilter] = useState('all');

    // Toast states
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('success'); // success, error, info

    // Category modal states
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [editFormData, setEditFormData] = useState({ name: '', description: '', is_active: 1 });

    // Delete modal states - Category
    const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
    const [deletingCategory, setDeletingCategory] = useState(null);

    // Delete modal states - Template
    const [showDeleteTemplateModal, setShowDeleteTemplateModal] = useState(false);
    const [deletingTemplate, setDeletingTemplate] = useState(null);

    const fetchCategories = useCallback(async () => {
        try {
            setLoadingCategories(true);
            const response = await axios.get('https://www.azzenterprises.com/api/categories/list');
            if (response.data.data) {
                setCategories(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
            showToastNotification('Failed to load categories.', 'error');
        } finally {
            setLoadingCategories(false);
        }
    }, []);

    const fetchTemplates = useCallback(async () => {
        try {
            setLoadingTemplates(true);
            const params = templateSearch ? `search=${encodeURIComponent(templateSearch)}` : '';
            const url = params ? `https://www.azzenterprises.com/api/templates/list?${params}` : 'https://www.azzenterprises.com/api/templates/list';
            const response = await axios.get(url);
            if (response.data.data) {
                setTemplates(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching templates:', err);
            showToastNotification('Failed to load templates.', 'error');
        } finally {
            setLoadingTemplates(false);
        }
    }, [templateSearch]);

    // Toast notification function
    const showToastNotification = (message, type = 'success') => {
        setToastMessage(message);
        setToastType(type);
        setShowToast(true);
    };

    // Auto hide toast after 4 seconds
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    // Delete functions
    const handleDeleteCategory = async () => {
        if (!deletingCategory) return;
        try {
            await axios.delete(`https://www.azzenterprises.com/api/categories/${deletingCategory.id}`);
            fetchCategories();
            closeDeleteCategoryModal();
            showToastNotification(`"${deletingCategory.name}" deleted successfully`, 'success');
        } catch (err) {
            console.error('Error deleting category:', err);
            showToastNotification('Failed to delete category.', 'error');
        }
    };

    const handleDeleteTemplate = async () => {
        if (!deletingTemplate) return;
        try {
            await axios.delete(`https://www.azzenterprises.com/api/templates/${deletingTemplate.id}`);
            fetchTemplates();
            closeDeleteTemplateModal();
            showToastNotification(`"${deletingTemplate.template_name}" deleted successfully`, 'success');
        } catch (err) {
            console.error('Error deleting template:', err);
            showToastNotification('Failed to delete template.', 'error');
        }
    };

    useEffect(() => {
        fetchCategories();
        fetchTemplates();
    }, [refreshKey, fetchCategories, fetchTemplates]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchTemplates();
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [templateSearch]);

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
        setTemplateSearch('');
    };

    const getStatusConfig = (status) => {
        switch (status) {
            case 'Active': return { bg: '#dcfce7', color: '#16a34a', iconColor: '#10b981' };
            case 'Inactive': return { bg: '#fef2f2', color: '#ef4444', iconColor: '#dc2626' };
            default: return { bg: '#f3f4f6', color: '#6b7280', iconColor: '#9ca3af' };
        }
    };

    const getFirstLetter = (name) => name ? name.charAt(0).toUpperCase() : '?';

    const filteredCategories = useMemo(() => {
        if (activeCategoryFilter === 'all') return categories;
        return categories.filter(cat =>
            cat.is_active === (activeCategoryFilter === 'active' ? 1 : 0)
        );
    }, [categories, activeCategoryFilter]);

    const openEditCategoryModal = useCallback((category) => {
        setEditingCategory(category);
        setEditFormData({
            name: category.name || '',
            description: category.description || '',
            is_active: category.is_active || 1
        });
        setShowCategoryModal(true);
    }, []);

    const closeEditModal = useCallback(() => {
        setShowCategoryModal(false);
        setEditingCategory(null);
        setEditFormData({ name: '', description: '', is_active: 1 });
    }, []);

    const openDeleteCategoryModal = useCallback((category) => {
        setDeletingCategory(category);
        setShowDeleteCategoryModal(true);
    }, []);

    const closeDeleteCategoryModal = useCallback(() => {
        setShowDeleteCategoryModal(false);
        setDeletingCategory(null);
    }, []);

    const openDeleteTemplateModal = useCallback((template) => {
        setDeletingTemplate(template);
        setShowDeleteTemplateModal(true);
    }, []);

    const closeDeleteTemplateModal = useCallback(() => {
        setShowDeleteTemplateModal(false);
        setDeletingTemplate(null);
    }, []);

    const handleSaveCategory = useCallback(async () => {
        if (!editFormData.name.trim()) {
            showToastNotification('Category name is required', 'error');
            return;
        }
        try {
            const url = editingCategory
                ? `https://www.azzenterprises.com/api/categories/${editingCategory.id}`
                : 'https://www.azzenterprises.com/api/categories';
            const method = editingCategory ? 'put' : 'post';
            await axios({ method, url, data: editFormData });
            fetchCategories();
            closeEditModal();
            showToastNotification(
                editingCategory
                    ? `"${editFormData.name}" updated successfully`
                    : `"${editFormData.name}" created successfully`,
                'success'
            );
        } catch (err) {
            console.error('Error saving category:', err);
            showToastNotification('Failed to save category.', 'error');
        }
    }, [editFormData, editingCategory, fetchCategories, closeEditModal]);

    const renderCategoryItem = (category) => {
        const status = category.is_active === 1 ? 'Active' : 'Inactive';
        const statusConfig = getStatusConfig(status);
        const firstLetter = getFirstLetter(category.name);

        return (
            <div className="category-item px-3 py-2 mb-2 rounded-3 bg-light hover-primary-bg transition-all d-flex align-items-center gap-3"
                style={{ fontSize: '0.875rem', cursor: 'pointer' }}
                key={category.id}>
                <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                    style={{
                        width: '36px', height: '36px',
                        backgroundColor: statusConfig.bg,
                        color: statusConfig.color,
                        fontSize: '0.75rem', fontWeight: '700'
                    }}>
                    {firstLetter}
                </div>
                <div className="flex-grow-1 min-w-0">
                    <div className="fw-semibold text-truncate mb-1" style={{ fontSize: '0.9rem' }}>
                        {category.name}
                    </div>
                    <div className="text-muted small">
                        {category.description || 'No description'}
                    </div>
                </div>
                <div className="d-flex gap-1 flex-shrink-0">
                    <button
                        className="btn btn-link p-1 text-primary rounded-2 shadow-none border-0"
                        title="Edit"
                        onClick={(e) => {
                            e.stopPropagation();
                            openEditCategoryModal(category);
                        }}
                    >
                        <FiEdit2 size={14} />
                    </button>
                    <button
                        className="btn btn-link p-1 text-danger rounded-2 shadow-none border-0"
                        title="Delete"
                        onClick={(e) => {
                            e.stopPropagation();
                            openDeleteCategoryModal(category);
                        }}
                    >
                        <FiTrash2 size={14} />
                    </button>
                </div>
            </div>
        );
    };

    const renderTemplateItem = (template) => {
        const status = template.status == '1' ? 'Active' : 'Inactive';
        const statusConfig = getStatusConfig(status);
        const firstLetter = getFirstLetter(template.template_name);

        return (
            <div className="template-item px-3 py-2 mb-2 rounded-3 bg-light hover-primary-bg transition-all d-flex align-items-center gap-3"
                style={{ fontSize: '0.875rem', cursor: 'pointer' }}
                key={template.id}>
                <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                    style={{
                        width: '36px', height: '36px',
                        backgroundColor: statusConfig.bg,
                        color: statusConfig.color,
                        fontSize: '0.75rem', fontWeight: '700'
                    }}>
                    {firstLetter}
                </div>
                <div className="flex-grow-1 min-w-0">
                    <div className="fw-semibold text-truncate mb-1" style={{ fontSize: '0.9rem' }}>
                        {template.template_name}
                    </div>
                    <div className="text-muted small" style={{ maxWidth: '200px' }}>
                        {template.subject || 'No subject'}
                    </div>
                </div>
                <Badge bg={status === 'Active' ? 'success' : 'secondary'} className="px-2 py-1 small">
                    {status}
                </Badge>
                <div className="d-flex gap-1 flex-shrink-0">
                    <button
                        className="btn p-1 text-primary hover-bg-light rounded-2 transition-all shadow-none border-0"
                        title="Edit"
                        onClick={(e) => {
                            e.stopPropagation();
                            // Edit template functionality can be added here
                        }}
                    >
                        <FiEdit2 size={14} />
                    </button>
                    <button
                        className="btn p-1 text-danger hover-bg-light rounded-2 transition-all shadow-none border-0"
                        title="Delete"
                        onClick={(e) => {
                            e.stopPropagation();
                            openDeleteTemplateModal(template);
                        }}
                    >
                        <FiTrash2 size={14} />
                    </button>
                </div>
            </div>
        );
    };

    const getFilterButtonStyle = (filter) => {
        const colors = {
            all: { bg: '#eff6ff', color: '#1e40af', border: '#3b82f6' },
            active: { bg: '#f0fdf4', color: '#15803d', border: '#10b981' },
            inactive: { bg: '#fef2f2', color: '#b91c1c', border: '#ef4444' }
        };
        const color = colors[filter];
        return {
            fontSize: '0.8rem',
            backgroundColor: activeCategoryFilter === filter ? color.bg : '#f8fafc',
            color: activeCategoryFilter === filter ? color.color : '#475569',
            border: activeCategoryFilter === filter ? `2px solid ${color.border}` : '1px solid transparent',
            padding: '0.375rem 0.75rem'
        };
    };

    return (
        <div className="min-vh-100 position-relative bg-light">
            <div className="container-fluid py-3 px-3 px-md-4">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-white rounded-3 shadow-sm">
                    <div className="d-flex align-items-center gap-3">
                        <FiBell size={28} className="text-primary" />
                        <div>
                            <div className="fw-bold fs-5 mb-1">Notification Templates</div>
                            <div className="text-muted fs-2xs">
                                {filteredCategories.length + templates.length} total • {categories.length} categories
                            </div>
                        </div>
                    </div>
                    <Button className="px-3 py-2 fw-semibold rounded-2 fs-2xs shadow-sm d-flex align-items-center gap-1"
                        variant="primary" onClick={handleRefresh}>
                        <FiSearch size={14} /> Refresh
                    </Button>
                </div>

                <div className="row g-4">
                    {/* Categories Sidebar */}
                    <div className="col-lg-4 col-md-5">
                        <div className="bg-white rounded-3 shadow-sm h-100">
                            <div className="p-4 pb-3 border-bottom">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold mb-0 fs-2xs">Categories</h6>
                                    <div className="d-flex gap-1">
                                        {['all', 'active', 'inactive'].map(filter => (
                                            <Button
                                                key={filter}
                                                className="border-0 rounded-2 fw-semibold fs-2xs transition-all px-2 py-1 flex-shrink-0"
                                                style={getFilterButtonStyle(filter)}
                                                onClick={() => setActiveCategoryFilter(filter)}
                                            >
                                                {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="p-0" style={{ maxHeight: '70vh', overflow: 'auto' }}>
                                {loadingCategories ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center py-12 text-center text-muted">
                                        <Spinner animation="border" size="sm" className="mb-2" />
                                        <div className="fs-6 fw-semibold mb-1">Loading categories</div>
                                    </div>
                                ) : filteredCategories.length === 0 ? (
                                    <div className="text-center py-12">
                                        <FiMail size={36} className="text-muted mb-3 opacity-75 mx-auto" />
                                        <h6 className="text-muted mb-2 fw-semibold">No categories found</h6>
                                        <Button variant="outline-primary" size="sm" className="px-3 py-1" onClick={fetchCategories}>
                                            Refresh Categories
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="p-3">
                                        {filteredCategories.map(renderCategoryItem)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Templates Main Area */}
                    <div className="col-lg-8 col-md-7">
                        <div className="bg-white rounded-3 shadow-sm h-100">
                            <div className="p-4 pb-3 border-bottom">
                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                    <h6 className="fw-bold mb-0 fs-2xs flex-grow-1">Templates</h6>
                                    <InputGroup size="sm" className="flex-grow-1" style={{ maxWidth: '280px' }}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search templates..."
                                            value={templateSearch}
                                            onChange={(e) => setTemplateSearch(e.target.value)}
                                            className="border-0 fs-2xs shadow-none py-2 rounded-2"
                                            style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
                                        />
                                    </InputGroup>
                                </div>
                            </div>
                            <div className="p-0" style={{ maxHeight: '70vh', overflow: 'auto' }}>
                                {loadingTemplates ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center py-12 text-center text-muted">
                                        <Spinner animation="border" size="sm" className="mb-2" />
                                        <div className="fs-6 fw-semibold mb-1">Loading templates</div>
                                    </div>
                                ) : templates.length === 0 ? (
                                    <div className="text-center py-12">
                                        <FiMail size={36} className="text-muted mb-3 opacity-75 mx-auto" />
                                        <h6 className="text-muted mb-2 fw-semibold">No templates found</h6>
                                        <p className="text-muted mb-3 fs-2xs">
                                            {templateSearch ? `No templates match "${templateSearch}"` : 'Create your first template'}
                                        </p>
                                        <Button variant="outline-primary" size="sm" className="px-3 py-1" onClick={fetchTemplates}>
                                            Refresh Templates
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="p-3">
                                        {templates.map(renderTemplateItem)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Category Edit Modal */}
            <Modal show={showCategoryModal} onHide={closeEditModal} size="md" centered backdrop="static">
                <Modal.Header className="px-4 py-3 border-0 pb-0">
                    <div className="d-flex align-items-center justify-content-between w-100">
                        <div className="d-flex align-items-center gap-2">
                            <div className="bg-primary-subtle rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: '32px', height: '32px' }}>
                                <FiEdit2 size={16} className="text-primary" />
                            </div>
                            <div>
                                <h6 className="mb-0 fw-bold fs-2xs">
                                    {editingCategory ? 'Edit Category' : 'New Category'}
                                </h6>
                                <div className="text-muted fs-2xs">
                                    {editingCategory ? `#${editingCategory.id}` : 'Create new category'}
                                </div>
                            </div>
                        </div>
                        <button className="btn p-1 text-muted hover-bg-light rounded-2" onClick={closeEditModal}>
                            <FiX size={18} />
                        </button>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4 pt-0">
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label className="fs-2xs fw-semibold text-muted mb-1">Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Category name"
                                value={editFormData.name}
                                onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                                className="fs-2xs rounded-2 shadow-none py-2"
                                style={{ border: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label className="fs-2xs fw-semibold text-muted mb-1">Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Optional description..."
                                value={editFormData.description}
                                onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                                className="fs-2xs rounded-2 shadow-none py-2"
                                style={{ border: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label className="fs-2xs fw-semibold text-muted mb-2 d-flex align-items-center gap-1">
                                Status
                            </Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Check
                                    type="radio"
                                    label="Active"
                                    checked={editFormData.is_active === 1}
                                    onChange={() => setEditFormData(prev => ({ ...prev, is_active: 1 }))}
                                    className="fs-2xs"
                                />
                                <Form.Check
                                    type="radio"
                                    label="Inactive"
                                    checked={editFormData.is_active === 0}
                                    onChange={() => setEditFormData(prev => ({ ...prev, is_active: 0 }))}
                                    className="fs-2xs"
                                />
                            </div>
                        </Form.Group>
                        <div className="d-flex gap-2">
                            <Button variant="primary" className="flex-grow-1 fs-2xs fw-semibold rounded-2 px-4" onClick={handleSaveCategory}>
                                <FiCheck size={14} className="me-1" /> Save Changes
                            </Button>
                            <Button variant="outline-secondary" className="fs-2xs fw-semibold rounded-2 px-3" onClick={closeEditModal}>
                                Cancel
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Delete Confirmation Modals - unchanged */}
            <Modal show={showDeleteCategoryModal} onHide={closeDeleteCategoryModal} size="md" centered>
                <Modal.Header className="px-4 py-3 border-0">
                    <div className="d-flex align-items-center gap-2">
                        <div className="bg-danger-subtle rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '32px', height: '32px' }}>
                            <FiTrash2 size={16} className="text-danger" />
                        </div>
                        <div>
                            <h6 className="mb-0 fw-bold fs-2xs">Delete Category</h6>
                            <div className="text-muted fs-2xs">Are you sure?</div>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4 pt-0">
                    <p className="text-muted mb-4 fs-2xs">
                        This action cannot be undone. This will permanently delete the category{' '}
                        <strong>"{deletingCategory?.name}"</strong> and all associated data.
                    </p>
                    <div className="d-flex gap-2 justify-content-end">
                        <Button
                            variant="outline-secondary"
                            className="fs-2xs fw-semibold rounded-2 px-4"
                            onClick={closeDeleteCategoryModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            className="fs-2xs fw-semibold rounded-2 px-4"
                            onClick={handleDeleteCategory}
                        >
                            Delete Category
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={showDeleteTemplateModal} onHide={closeDeleteTemplateModal} size="md" centered>
                <Modal.Header className="px-4 py-3 border-0">
                    <div className="d-flex align-items-center gap-2">
                        <div className="bg-danger-subtle rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '32px', height: '32px' }}>
                            <FiTrash2 size={16} className="text-danger" />
                        </div>
                        <div>
                            <h6 className="mb-0 fw-bold fs-2xs">Delete Template</h6>
                            <div className="text-muted fs-2xs">Are you sure?</div>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4 pt-0">
                    <p className="text-muted mb-4 fs-2xs">
                        This action cannot be undone. This will permanently delete the template{' '}
                        <strong>"{deletingTemplate?.template_name}"</strong>.
                    </p>
                    <div className="d-flex gap-2 justify-content-end">
                        <Button
                            variant="outline-secondary"
                            className="fs-2xs fw-semibold rounded-2 px-4"
                            onClick={closeDeleteTemplateModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            className="fs-2xs fw-semibold rounded-2 px-4"
                            onClick={handleDeleteTemplate}
                        >
                            Delete Template
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            <ToastContainer
                className="position-fixed"
                position="top-end"
                style={{ top: '20px', right: '20px' }}
            >
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    delay={2000}
                    autohide
                    bg={toastType === 'success' ? 'success' : 'danger'}
                >
                    <Toast.Header
                        className={`${toastType === 'success' ? 'bg-success' : 'bg-danger'
                            } text-white`}
                        closeButton={false}
                    >
                        <FiCheckCircle size={16} className="me-2 text-white" />
                        <strong className="me-auto text-white">
                            {toastType === 'success' ? 'Success' : 'Error'}
                        </strong>
                    </Toast.Header>

                    <Toast.Body className="fs-2xs text-white">
                        {toastMessage}
                    </Toast.Body>
                </Toast>
            </ToastContainer>


            <FabMenu onRefresh={handleRefresh} />
        </div>
    );
};

export default Template;
